import React, { useCallback, useEffect, useState, useRef } from "react";
import PropTypes from "prop-types";
import I18n from "i18n-js";
import { Button } from "@duxton-web/ui";
import "./index.less";
import { generateUTMParams } from "./util";

import LeftCupImg from "./assets/left-cup.svg";
import LeftChickenImg from "./assets/left-chicken.svg";
import LeftBuyImg from "./assets/left-buy.svg";
import LeftMFCMenuImg from "./assets/left-mfc-menu.svg";

import { directToURl, ADSUMP, isRoleCashier } from "../utils";
import { haveAccessToFeature } from "../utils/FeatureHelper";
import { hasBannerUpsell } from "../constants";

const useOpenStatus = () => {
  const initialOpen = haveAccessToFeature(hasBannerUpsell) && !isRoleCashier();
  const [open, setOpen] = useState(initialOpen);

  return [open, setOpen];
};

export const MarketingBannerV2 = (props) => {
  const { className, onClick, onOpen, tab, subtab, trackEvent, variant } =
    props;

  const eventType = variant === "upsellMFCMenu" ? "promo" : "Upsell";
  const [open, setOpen] = useOpenStatus();

  const { title, description, leftImg, cta, message, CTA_name } = {
    title: {
      finance: I18n.t("apt_mm_upsell_heading_store_visib"),
      menu: I18n.t("apt_mm_upsell_heading_menu_shoutout"),
      inventory: I18n.t("apt_mm_upsell_heading_menu_shoutout"),
      customerGetMore: I18n.t("apt_mm_upsell_heading_more_customers"),
      customerAttractNew: I18n.t("apt_mm_upsell_heading_attract_new_customers"),
      customerBringBack: I18n.t("apt_mm_upsell_heading_bring_customers_back"),
      upsellMFCMenu: I18n.t("apt_mm_menu_title_item_discount_upsell"),
    }[variant],

    description: {
      finance: I18n.t("apt_mm_upsell_body_store_visib"),
      menu: I18n.t("apt_mm_upsell_body_menu_shoutout"),
      inventory: I18n.t("apt_mm_upsell_body_menu_shoutout"),
      customerGetMore: I18n.t("apt_mm_upsell_body_more_customers"),
      customerAttractNew: I18n.t("apt_mm_upsell_body_attract_new_customers"),
      customerBringBack: I18n.t("apt_mm_upsell_body_bring_customers_back"),
      upsellMFCMenu: I18n.t("apt_mm_item_discount_upsell"),
    }[variant],

    leftImg: {
      finance: LeftCupImg,
      menu: LeftChickenImg,
      inventory: LeftChickenImg,
      customerGetMore: LeftBuyImg,
      customerAttractNew: LeftBuyImg,
      customerBringBack: LeftBuyImg,
      upsellMFCMenu: LeftMFCMenuImg,
    }[variant],

    // instrumentation only
    message: {
      finance: "Soar to the top of the search results for more daily orders.",
      menu: "Drive more orders by running a campaign.",
      inventory: "Drive more orders by running a campaign.",
      customerGetMore:
        "Merchants like you have gained more customers through marketing campaigns.",
      customerAttractNew:
        "Get more orders from new customers every day through marketing campaigns.",
      customerBringBack:
        "Re-engage with past customers to encourage them to order again.",
      upsellMFCMenu: "Boost your item sales with item discount.",
    }[variant],

    cta: {
      upsellMFCMenu: I18n.t("apt_mm_acquisition_button_try_marketing_manager"),
    }[variant],

    CTA_name: "Create a campaign",
  };

  const _onClick = useCallback(() => {
    if (tab && trackEvent) {
      trackEvent(
        "MARKETING_BANNER",
        "PRODUCT_UPSELL.BANNER_CLICK",
        JSON.stringify({
          message,
          CTA_name,
          portal_tab: tab,
          portal_subtab: subtab,
          status: "Success",
          error_msg: null,
          type: eventType,
        })
      );
    }
    if (onClick) {
      onClick();
    }

    const params = generateUTMParams(tab, subtab);
    directToURl(null, ADSUMP, params);
  }, [onClick, tab, subtab, trackEvent, message, CTA_name]);

  const viewTracked = useRef(false);
  useEffect(() => {
    if (open && tab && subtab && trackEvent && !viewTracked.current) {
      viewTracked.current = true;
      trackEvent(
        "MARKETING_BANNER",
        "PRODUCT_UPSELL.BANNER_VIEW",
        JSON.stringify({
          message,
          CTA_name,
          portal_tab: tab,
          portal_subtab: subtab,
          type: eventType,
        })
      );
    }
  }, [open, tab, subtab, trackEvent, message, CTA_name]);

  useEffect(() => {
    if (open && onOpen) {
      onOpen();
    }
  }, [open, onOpen]);

  if (!open) return null;

  return (
    <div className={`troy-marketing-banner-v2 ${className}`}>
      <img className="troy-marketing-banner__img-v2" src={leftImg} />

      <div className="troy-marketing-banner__text-container-v2">
        <p className="troy-marketing-banner__header">{title}</p>
        <p className="troy-marketing-banner__description">{description}</p>
      </div>

      <Button
        type="secondary"
        size="small"
        className="troy-marketing-banner__cta-v2"
        onClick={_onClick}
      >
        {cta || I18n.t("apt_mm_upsell_button_create_a_campaign")}
      </Button>
    </div>
  );
};

MarketingBannerV2.propTypes = {
  className: PropTypes.string,

  onClick: PropTypes.func,
  onOpen: PropTypes.func,

  variant: PropTypes.oneOf([
    "finance",
    "menu",
    "inventory",
    "customerGetMore",
    "customerAttractNew",
    "customerBringBack",
    "upsellMFCMenu",
  ]),

  tab: PropTypes.oneOf(["Insights", "Finance", "Menu", "Inventory"]),
  subtab: PropTypes.oneOf([
    "Sales", // insights
    "Menu", // insights
    "Customers", // insights
    "Transactions", // finance
    "Menu Overview", // menu
    "Item", // inventory
    "MFC", // MFC
  ]),

  trackEvent: PropTypes.func,
};
